/**
 * client.js — MissionsClient: async operations over an injected BlobHub session API.
 *
 * No React. Takes a plain dependency object so it can be reused in a standalone app.
 *
 * Constructor:
 *   new MissionsClient({ api, sessionManager, revisionId, getSessionId })
 *
 * - api           — the Api class instance (context/api.js)
 * - sessionManager — SessionManager instance (playground/session/manager.js)
 * - revisionId    — string; the current revision id
 * - getSessionId  — () => string; thunk returning the current root session id
 */

import Navigation from "context/nav";

import {
  CELL_WORKFLOWS_ALIAS,
  WORKSPACES_ALIAS,
  HEARTBEAT_STATE_ALIAS,
  MISSION_STATUS,
  missionAlias,
  isMissionAlias,
  missionIdFromAlias,
  cellMetaThreadAlias,
  cellPrefix,
  mintCellId
} from "./schema";

// Page size for the exhausting graph element listing — the loop fetches everything,
// so the largest legal page means the fewest round-trips.
const GRAPH_PAGE_LIMIT = 1000;

// Page size for the exhausting meta-thread listing.
const THREAD_PAGE_LIMIT = 100;

// Page size for the INTERACTIVE reads behind the cell detail view. Small enough that
// "load older" is a real control rather than decoration, large enough that an ordinary
// cell arrives in one page.
const THREAD_VIEW_LIMIT = 25;
// What the mission dock's meta-thread preview shows. See `previewMetaThread`.
const META_PREVIEW_LIMIT = 10;

/**
 * Generate a client-side graph element id with the given prefix. The server
 * honours a client-provided element_id on add_vertex / add_edge (the graph
 * import path and createMission both rely on this), so generating ids here lets
 * callers insert the new element into the local store immediately — and delete
 * it again — without waiting for a reload.
 * @param {string} prefix
 * @returns {string}
 */
function generateElementId(prefix) {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

export default class MissionsClient {
  constructor({ api, sessionManager, revisionId, getSessionId, canMutate }) {
    this._api = api;
    this._sessionManager = sessionManager;
    this._revisionId = revisionId;
    this._getSessionId = getSessionId;
    // Absent means unrestricted: a test double or an embedding host that does not express the seam
    // gets today's behaviour. The real widget always supplies it (common/workNodeBase.js).
    this._canMutate = canMutate;
  }

  /**
   * The single choke point for every write, behind the affordances the surfaces already hide.
   *
   * Each mutating method calls this first. Hiding a control is what a person sees; this is what
   * makes the widget correct — a handler reached by a path nobody thought of, a stale closure, a
   * session closed while a modal was open. It THROWS rather than resolving to nothing, because
   * every caller here returns a created element that its caller then reads: a silent undefined
   * would surface as a crash somewhere unrelated, and several call sites apply an optimistic local
   * change that only a rejection prevents.
   */
  _assertMutable(operation) {
    if (this._canMutate && !this._canMutate()) {
      throw new Error(`Cannot ${operation}: this revision or session is read-only.`);
    }
  }

  // ---------------------------------------------------------------------------
  // Session resolution — F2 seam: single root session for now
  // ---------------------------------------------------------------------------

  /**
   * Return the session id to use for graph/thread ops for a given mission.
   * Currently returns the root session id (the F2 seam: one session per instance).
   * @param {string} _missionId - reserved for future multi-session support
   * @returns {string}
   */
  resolveSession(_missionId) {
    return this._getSessionId();
  }

  // ---------------------------------------------------------------------------
  // Read operations
  // ---------------------------------------------------------------------------

  /**
   * List all missions in the current session.
   * Filters sessionObjects by isMissionAlias and reads each mission's display
   * metadata (title, description, …) from the graph object's OWN metadata
   * (value.graph.metadata) — not from a vertex.
   * @returns {Promise<Array>}
   */
  async listMissions() {
    const sessionObjects = this._sessionManager.sessionObjects || [];
    return sessionObjects
      .filter(obj => isMissionAlias(obj.alias))
      .map(obj => {
        const missionId = missionIdFromAlias(obj.alias);
        const val = this._sessionManager.getSessionData(obj.alias);
        const meta = (val && val.graph && val.graph.metadata) || {};
        return { id: missionId, ...meta };
      });
  }

  /**
   * List all cells (vertices labeled "cell") for a mission.
   * @param {string} missionId
   * @returns {Promise<Array>}
   */
  async listCells(missionId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    try {
      const elements = await this._listAllGraphElements(sessionId, alias, "vertex");
      return elements.filter(el => el.labels && el.labels.includes("cell"));
    } catch (e) {
      return [];
    }
  }

  /**
   * List all "depends_on" edges for a mission's graph.
   * Fetches the graph elements (which includes both vertices and edges) and
   * filters to edges with label "depends_on".
   *
   * Each edge keeps its `element_id` (and `rev` when present) so the editor can
   * delete it later via a delete_edge op, which is keyed by element_id.
   * @param {string} missionId
   * @returns {Promise<Array<{ element_id: string, from_id: string, to_id: string, rev?: number }>>}
   */
  async listDependsOn(missionId) {
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    try {
      const elements = await this._listAllGraphElements(sessionId, alias, "edge");
      return elements
        .filter(el => el.type === "edge" && el.label === "depends_on")
        .map(el => ({ element_id: el.element_id, from_id: el.from_id, to_id: el.to_id, rev: el.rev }));
    } catch (e) {
      return [];
    }
  }

  /**
   * Fetch EVERY graph element of a given type, following the cursor across pages. The list API
   * returns one page at a time (`data.elements` + an opaque `data.cursor`, null on the last page);
   * a single un-paged call only sees the first page — which is why large missions previously
   * rendered just a subset of their cells.
   * @param {string} sessionId
   * @param {string} alias
   * @param {"vertex"|"edge"} type
   * @returns {Promise<Array>}
   */
  async _listAllGraphElements(sessionId, alias, type) {
    const all = [];
    let cursor = null;
    // Defensive page bound so a never-clearing cursor can't spin forever.
    for (let page = 0; page < 1000; page++) {
      const response = await this._api.listSessionGraphElements(
        this._revisionId, sessionId, alias, { type, cursor, limit: GRAPH_PAGE_LIMIT }
      );
      const data = (response && response.data) || {};
      all.push(...(data.elements || []));
      cursor = data.cursor || null;
      if (!cursor) break;
    }
    return all;
  }

  /**
   * Retrieve the cell-workflow, workspace, agent and harness template libraries from the session.
   * Each collection is returned as a list; absent or unrecognised data yields [].
   * @returns {Promise<{ workflows: Array, workspaces: Array, agents: Array, harnesses: Array }>}
   */
  async getTemplates() {
    const wfValue = this._sessionManager.getSessionData(CELL_WORKFLOWS_ALIAS);
    const wsValue = this._sessionManager.getSessionData(WORKSPACES_ALIAS);
    // Both libraries are `message` objects whose metadata is a MAP holding the
    // list under a named key — `metadata.cell_workflows` / `metadata.workspaces`
    // (data-structures spec §4.8/§4.9).
    const parse = (val, key) => {
      if (!val || val.type !== 'message') return [];
      const meta = (val.message && val.message.metadata) || {};
      return Array.isArray(meta[key]) ? meta[key] : [];
    };
    return {
      workflows: parse(wfValue, 'cell_workflows'),
      workspaces: parse(wsValue, 'workspaces'),
      // Third list on the SAME object as the workflows — the palette an author edits in one place.
      agents: parse(wfValue, 'agents'),
      // Fourth list on that same object: the vocabulary the `agents` beside it are built OUT of,
      // read by the popover's custom section. Absent on an instance predating the palette key, and
      // [] is the honest answer there — no vocabulary means no section, not a broken one.
      harnesses: parse(wfValue, 'harnesses'),
    };
  }

  /**
   * Retrieve the heartbeat state from the session.
   * @returns {Promise<any>}
   */
  async getHeartbeat() {
    return this._sessionManager.getSessionData(HEARTBEAT_STATE_ALIAS) || null;
  }

  /**
   * List EVERY thread item in a cell's meta-thread, following the opaque cursor across pages
   * (chronological order). A single un-paged call only sees the server's first page (default 10
   * items), which previously left later thread items invisible to Mission Control.
   * @param {string} missionId
   * @param {string} cellId
   * @returns {Promise<Array>}
   */
  /**
   * Is there a thread at this alias yet?
   *
   * A cell's meta-thread is created by the ORCHESTRATOR when the cell is activated, and a run's
   * transcript when the run is dispatched. Until then the alias names nothing, and asking the
   * platform for its items is answered `invalid_thread_envelope` — correctly. The readers below
   * have always absorbed that and returned an empty page, so the SURFACE was right; what was wrong
   * is that pressing Activate put a failed request in the network log and an error in the console
   * every time, for a state that is not a failure at all. A cell that has not run has no thread.
   *
   * Answered from the session's own object list, which is what the object tree reads too, so the
   * two agree about what exists. An EMPTY list is not evidence of absence — it is what a session
   * looks like before its content has been read — so the request still goes out then, and the
   * readers absorb the answer exactly as before.
   */
  _threadExists(alias) {
    const objects = this._sessionManager.sessionObjects || [];
    if (objects.length === 0) return true;
    return objects.some(object => object && object.alias === alias);
  }

  /**
   * The TAIL of a cell's meta-thread — what the panel beside the mission map previews.
   *
   * Ten, because that rail is 16rem wide and a preview that runs past the fold has stopped being
   * one: a long-running cell put its whole history in there, and the newest record — the one saying
   * what the cell is doing now — sat at the bottom of a wall nobody scrolls. The cell's own screen
   * is where a thread is read, and it pages back through all of it.
   *
   * One request, not a paged sweep. This used to read every page of the thread (up to a thousand)
   * on every panel open and after every action, to render what is now ten items.
   *
   * @returns {Promise<{ items: Array, more: boolean }>} oldest-first, and whether older ones exist
   */
  async previewMetaThread(missionId, cellId) {
    const alias = cellMetaThreadAlias(missionId, cellId);
    // An absent thread is an ordinary state — a DRAFT cell has none until it is activated — and
    // `_pageThread` answers it with an empty page rather than a throw.
    const { items, cursor } = await this._pageThread(alias, null, META_PREVIEW_LIMIT);
    return { items, more: !!cursor };
  }

  /**
   * Create a new mission graph session object. The mission's display metadata
   * (title, description) lives in the graph object's OWN metadata, not in a vertex.
   * Cells are added as vertices via apply_session_graph_mutations afterwards.
   * @param {{ id: string, title: string, description: string }} options
   * @returns {Promise<any>}
   */
  async createMission({ id, title, description }) {
    this._assertMutable('create a mission');
    const alias = missionAlias(id);
    // The caller mints; this is the last line of defence. `upload_session_object` OVERWRITES by
    // alias, so a collision would replace a live mission rather than fail — there is no conditional
    // create to lean on. Refusing is the only safe answer once the id is already taken.
    if ((this._sessionManager.sessionObjects || []).some(object => object.alias === alias)) {
      throw new Error(`mission ${id} already exists`);
    }
    return this._sessionManager.uploadSessionObject(alias, {
      type: "graph",
      graph: {
        attributes: { id, created_at: new Date().toISOString() },
        // `status` is not decoration: the heartbeat gates on it — `if mission.status != "active"`
        // it summarises the mission with zero cells and never reconciles one. A mission created
        // without it is therefore INVISIBLE to the orchestrator, cells and all, while looking
        // perfectly healthy in this UI. Every other writer of a mission sets it; this one did not.
        metadata: { title, description, status: MISSION_STATUS.ACTIVE }
      }
    });
  }

  /**
   * Update a mission's display metadata (title, description, and/or its default workspace).
   *
   * A mission's display fields live in the graph object's OWN metadata
   * (value.graph.metadata), NOT in a vertex. This reads the current graph
   * envelope, merges the changed field(s) into its metadata — preserving the
   * existing attributes and any other metadata keys — and re-uploads the
   * envelope.
   *
   * Re-uploading the graph envelope is NON-DESTRUCTIVE to the mission's cells:
   * the graph's vertices/edges are a separate server-side store, mutated only
   * through apply_session_graph_mutations; the envelope value carries just
   * attributes + metadata (it never holds the vertices). This is exactly the
   * shape the platform's own graph editor saves (editor/details/content.js
   * treats a "graph" value as envelope-only — attributes + metadata).
   * @param {string} missionId
   * @param {{ title?: string, description?: string, workspace?: { type: string, work_folder: string } | null }} changed
   * @returns {Promise<any>}
   */
  async setMissionProps(missionId, changed) {
    this._assertMutable('change a mission');
    const alias = missionAlias(missionId);
    const current = this._sessionManager.getSessionData(alias) || {};
    const graph = current.graph || {};
    const attributes = graph.attributes || {};
    const metadata = { ...(graph.metadata || {}), ...changed };
    return this._sessionManager.uploadSessionObject(alias, {
      type: "graph",
      graph: { attributes, metadata }
    });
  }

  /**
   * Add a new cell (with status "draft") to a mission's graph.
   *
   * ONE write: the vertex. A draft cell has no meta-thread object at all until it is activated.
   *
   * **Where the other two went.** This used to write three times — the vertex, the meta-thread
   * envelope, and a bootstrap `info: created` record — because `post_session_thread_item` rejects
   * with `invalid_thread_envelope` rather than auto-creating, so a cell whose meta-thread object did
   * not exist took the ORCHESTRATOR's first record down with it: the tick promoted the cell,
   * dispatched its run, threw on the record, and died before committing the vertex, leaving the cell
   * frozen at its old status with an empty feed, an orphaned run thread and a worker looping on it.
   * That hazard is real and is now handled where it belongs — the orchestrator creates the envelope
   * and posts `created` when it ACTIVATES the cell, which is also the moment the title stops being
   * editable, so that record can carry the title again. (It could not here: the meta-thread is
   * append-only with no upsert, so a copy taken at creation could never be corrected, and a cell
   * renamed while still a draft showed its first name in the feed for ever.)
   *
   * Every surface that reads a meta-thread therefore has to treat "no object yet" as an ordinary
   * state — a cell that has not run has no activity — rather than as an error. See `_pageThread`,
   * which returns what it has instead of throwing.
   *
   * Generates the cell's element_id client-side and returns the created cell
   * object ({ element_id, labels, props }) so the caller can insert it into the
   * local store immediately — no reload required.
   * @param {string} missionId
   * @param {{ title: string, cellWorkflow: string }} cellProps - plus any additional props
   * @returns {Promise<{ element_id: string, labels: string[], props: Object }>}
   */
  async addCell(missionId, { title, cellWorkflow, ...rest }) {
    this._assertMutable('add a cell');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const elementId = mintCellId(title);
    // cell_workflow is only set when a workflow is explicitly given; inherit-mode cells (the default —
    // see the panels' *_mode props) carry no cell_workflow name. Structural modes ride in via ...rest.
    //
    // `log`, `usage` and `request` are part of a cell's shape, not optional extras — the
    // orchestrator appends to the log on its first write and rolls usage into it, and a vertex that
    // omits them is one the design does not describe.
    const props = { title, status: "draft", log: [], usage: {}, request: null, ...rest };
    if (cellWorkflow) props.cell_workflow = cellWorkflow;
    await this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "add_vertex",
          labels: ["cell"],
          element_id: elementId,
          props
        }
      ]
    );

    return { element_id: elementId, labels: ["cell"], props };
  }

  /**
   * Update arbitrary props on an existing cell vertex.
   * @param {string} missionId
   * @param {string} cellId
   * @param {Object} props
   * @param {string} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async setCellProps(missionId, cellId, props, ifRev) {
    this._assertMutable('change a cell');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = {
      op: "set_vertex_props",
      element_id: cellId,
      props
    };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [op]
    );
  }

  /**
   * Add a "depends_on" edge between two cells.
   *
   * Generates the edge's element_id client-side and returns the created edge
   * ({ element_id, from_id, to_id }) so the caller can insert it into the local
   * store immediately and delete it later by element_id — no reload required.
   * @param {string} missionId
   * @param {string} fromId - id of the dependent cell
   * @param {string} toId - id of the dependency cell
   * @returns {Promise<{ element_id: string, from_id: string, to_id: string }>}
   */
  async addDependsOn(missionId, fromId, toId) {
    this._assertMutable('add a dependency');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const elementId = generateElementId("e");
    await this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "add_edge",
          label: "depends_on",
          element_id: elementId,
          from_id: fromId,
          to_id: toId
        }
      ]
    );
    return { element_id: elementId, from_id: fromId, to_id: toId };
  }

  /**
   * Delete everything a cell owns in the session namespace: its meta thread, every run's transcript
   * and every artifact those runs published.
   *
   * Deleting the VERTEX alone left all of it behind. Nothing else references a cell's objects — the
   * only path to them is the cell — so what stayed was unreachable: invisible in Mission Control,
   * counted against the session, and impossible to find again except by knowing the dead cell's id.
   *
   * Sequential, not concurrent. The count is what the caller shows a person, and a progress report
   * that is really "all of them are in flight" says nothing; a cell that published fifty artifacts
   * is also not something to fire fifty deletes at.
   *
   * `deleteSessionObject` reports its own failures through the session manager's progress channel
   * and RESOLVES either way, so the promise cannot be trusted to mean success. The post-condition
   * is checked instead — is the alias still in the session's object list — which is true of any way
   * it might fail, including ones the manager does not model.
   *
   * `onProgress` fires once per object, as that object STARTS — `done` is its 1-based ordinal, so a
   * caller reads "deleting 2/5 <alias>" straight off it. Reporting on completion instead would name
   * the object that just finished while a different one is in flight, which is the wrong tense for
   * the only thing anybody watches this for. What each object ended up as is in the return value.
   *
   * @param {string} missionId
   * @param {string} cellId
   * @param {(progress: { done: number, total: number, alias: string }) => void} [onProgress]
   * @returns {Promise<{ deleted: string[], failed: string[] }>}
   */
  async deleteCellObjects(missionId, cellId, onProgress) {
    this._assertMutable('delete a cell');
    const aliases = this.listCellObjectAliases(missionId, cellId);
    const deleted = [];
    const failed = [];
    for (const [index, alias] of aliases.entries()) {
      if (onProgress) onProgress({ done: index + 1, total: aliases.length, alias });
      try {
        await this._sessionManager.deleteSessionObject(alias);
      } catch (_e) {
        // Fall through to the post-condition: a rejection is one way to fail, not the only one.
      }
      const present = (this._sessionManager.sessionObjects || [])
        .some(object => object.alias === alias);
      (present ? failed : deleted).push(alias);
    }
    return { deleted, failed };
  }

  /**
   * Delete a cell VERTEX from a mission's graph. The server removes incident edges as part of the
   * delete_vertex op — but nothing at all in the session namespace, which is what deleteCellObjects
   * above is for. Callers that mean "delete this cell" want both, in that order.
   * @param {string} missionId
   * @param {string} cellId - the cell's element_id
   * @param {number} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async deleteCell(missionId, cellId, ifRev) {
    this._assertMutable('delete a cell');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = { op: "delete_vertex", element_id: cellId };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(this._revisionId, sessionId, alias, [op]);
  }

  /**
   * Delete a depends_on edge from a mission's graph by its element_id.
   * @param {string} missionId
   * @param {string} edgeId - the edge's element_id
   * @param {number} [ifRev] - optional optimistic-concurrency revision guard
   * @returns {Promise<any>}
   */
  async deleteEdge(missionId, edgeId, ifRev) {
    this._assertMutable('delete a dependency');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    const op = { op: "delete_edge", element_id: edgeId };
    if (ifRev !== undefined) {
      op.if_rev = ifRev;
    }
    return this._api.applySessionGraphMutations(this._revisionId, sessionId, alias, [op]);
  }

  /**
   * Activate a cell — set its status to "planned".
   * @param {string} missionId
   * @param {string} cellId
   * @returns {Promise<any>}
   */
  async activateCell(missionId, cellId) {
    this._assertMutable('activate a cell');
    const sessionId = this.resolveSession(missionId);
    const alias = missionAlias(missionId);
    return this._api.applySessionGraphMutations(
      this._revisionId,
      sessionId,
      alias,
      [
        {
          op: "set_vertex_props",
          element_id: cellId,
          props: { status: "planned" }
        }
      ]
    );
  }

  /**
   * Every session-object alias the browser currently knows beneath one cell.
   *
   * Read from the session manager's object list rather than the network: it is already maintained
   * live over the WebSocket, so an artifact published a second ago is here without a round-trip.
   * @param {string} missionId
   * @param {string} cellId
   * @returns {Array<string>}
   */
  listCellObjectAliases(missionId, cellId) {
    const prefix = cellPrefix(missionId, cellId);
    return (this._sessionManager.sessionObjects || [])
      .map(object => object.alias)
      .filter(alias => typeof alias === 'string' && alias.startsWith(prefix));
  }

  /**
   * Every item on one cell session's thread — the raw harness transcript, chronological.
   *
   * Paged to exhaustion for the same reason the meta-thread is: the server's default page is TEN
   * items, and a transcript is the longest thread in the system.
   * @param {string} alias - the session thread alias
   * @returns {Promise<Array>}
   */
  async listSessionThread(alias) {
    const sessionId = this._getSessionId();
    const all = [];
    if (!this._threadExists(alias)) return all;
    let cursor = null;
    try {
      for (let page = 0; page < 1000; page++) {
        const options = { ascending: true, limit: THREAD_PAGE_LIMIT };
        if (cursor) options.cursor = cursor;
        const response = await this._api.threadItemsList(this._revisionId, sessionId, alias, options);
        const data = (response && response.data) || {};
        all.push(...(data.items || []));
        cursor = data.cursor || null;
        if (!cursor) break;
      }
    } catch (e) {
      return all;
    }
    return all;
  }

  /**
   * One page of a thread, newest-first on the wire and oldest-first in the result.
   *
   * The exhausting `listSessionThread` above is right for a machine that needs everything; a person
   * opening a long-running cell wants the tail. Ten is the server's own default page and far too few
   * to READ, so the view's page size is explicit — and a caller previewing rather than reading passes
   * its own (`previewMetaThread`).
   * @param {string} alias
   * @param {string|null} cursor
   * @param {number} [limit]
   * @returns {Promise<{ items: Array, cursor: string|null }>}
   */
  async _pageThread(alias, cursor, limit = THREAD_VIEW_LIMIT) {
    if (!this._threadExists(alias)) return { items: [], cursor: null };
    const sessionId = this._getSessionId();
    const options = { ascending: false, limit };
    // A null cursor means EXHAUSTED and must be omitted rather than sent back.
    if (cursor) options.cursor = cursor;
    try {
      const response = await this._api.threadItemsList(this._revisionId, sessionId, alias, options);
      const data = (response && response.data) || {};
      return { items: (data.items || []).slice().reverse(), cursor: data.cursor || null };
    } catch (e) {
      return { items: [], cursor: null };
    }
  }

  pageMetaThread(missionId, cellId, cursor) {
    return this._pageThread(cellMetaThreadAlias(missionId, cellId), cursor);
  }

  pageSessionThread(alias, cursor) {
    return this._pageThread(alias, cursor);
  }

  /**
   * Everything on a thread created after `since`, oldest-first — the tail's catch-up read.
   *
   * A watch notification says only THAT the thread moved, so a view asks what it missed rather than
   * trusting a payload. That is what makes it right when several items land between two reads, and
   * when a notification is dropped altogether.
   *
   * `since` null means "everything", which is the first read of a thread nothing has been seen on.
   */
  async listThreadItemsSince(alias, since) {
    const sessionId = this._getSessionId();
    const options = { ascending: true, limit: THREAD_VIEW_LIMIT };
    if (since) options.createdSince = since;
    try {
      const response = await this._api.threadItemsList(this._revisionId, sessionId, alias, options);
      return ((response && response.data) || {}).items || [];
    } catch (e) {
      return [];
    }
  }

  /**
   * The display name for a `user_id`, resolved against the api's user cache.
   *
   * Attribution matters more here than in an ordinary chat: three parties write to a cell's threads
   * — the orchestrator, the worker and a human — each under its OWN identity, and which of them
   * wrote an item is often the whole question. A raw uuid answers it only for someone who has
   * memorised three uuids.
   *
   * Returns a name immediately when the cache has one, and otherwise the id's tail plus a
   * background fetch; `onResolved` repaints when that lands. Misses are remembered so a deleted or
   * unreachable account is not re-fetched on every render.
   */
  authorName(userId, onResolved) {
    if (!userId) return 'unknown';
    const cached = this._api.usersCacheById ? this._api.usersCacheById[userId] : null;
    if (cached) {
      const user = (cached.data && cached.data.user) || {};
      return user.name || user.alias || user.email || userId.slice(-5);
    }
    if (!this._authorFetches) this._authorFetches = {};
    if (!this._authorFetches[userId]) {
      this._authorFetches[userId] = this._api.usersIdGet(userId).then(
        () => { if (onResolved) onResolved(); },
        () => { /* keep the entry: a miss must not be retried on every render */ }
      );
    }
    return userId.slice(-5);
  }

  /**
   * The profile path for a `user_id`, or null when there is nothing to link to.
   * Prefers the account's alias for a readable URL, exactly as the thread widget does.
   */
  authorProfilePath(userId) {
    if (!userId) return null;
    const cached = this._api.usersCacheById ? this._api.usersCacheById[userId] : null;
    const user = cached && cached.data ? cached.data.user || cached.data : null;
    return Navigation.userProfilePath((user && user.alias) || userId);
  }

  /** Read one session object's value (a session thread envelope, or an artifact). */
  getObject(alias) {
    return this._sessionManager.getSessionData(alias) || null;
  }

  /**
   * Watch ONE thread for new items — the alias-scoped `session_thread_item_posted` event.
   *
   * The platform emits this and nothing else when an item lands: posting an item does NOT write the
   * session object, so it raises no `session_object_modified` and the session manager's broad
   * "something changed" listener never fires for it. A surface that wants to know an item arrived
   * has to ask for that alias by name — which is what the playground's own thread widget does.
   *
   * Both methods are no-ops against an adapter that provides no thread listeners; the sessionManager
   * this client takes is duck-typed (see the class doc), and a caller must not have to check.
   * @param {string} alias
   * @param {(payload: { alias: string, item_id: string }) => void} callback
   */
  watchThread(alias, callback) {
    const manager = this._sessionManager;
    if (!manager || !manager.addThreadEventListener) return;
    manager.addThreadEventListener(alias, callback);
  }

  /** Stop watching — the exact `alias`/`callback` pair handed to `watchThread`. */
  unwatchThread(alias, callback) {
    const manager = this._sessionManager;
    if (!manager || !manager.removeThreadEventListener) return;
    manager.removeThreadEventListener(alias, callback);
  }

  /**
   * Answer a GATE — an `external_input` step, or the review request an unroutable run raised.
   *
   * The record IS the answer: its `label` selects the outgoing edge and its `content` becomes the
   * run's content, carryover-visible to whatever runs next. `step` and `run` come off the request so
   * the tag cannot drift from the cursor, and the orchestrator consumes only the EARLIEST answer for
   * that pair — a double-submit cannot re-route a run that has already advanced.
   *
   * **This is the whole record of the answer, and it used to be half of one.** The orchestrator
   * posted its own event beside it saying a gate had been answered — one row naming the decision and
   * a second saying a decision had been made. It posts none for a gate now; `source: external` is
   * what marks this one as a person's, against the `system` ones the orchestrator writes when it
   * records what a worker's prompt bridge read back from an in-flight answer.
   *
   * That is also why the `event` is written HERE. Every meta record carries the verb for what it
   * did, and the orchestrator stamps `answered` on the in-flight half of this pair — but it cannot
   * stamp one on a record it never writes. Without it, the one record a person's own decision
   * produces would be the only row in the feed with a hole where every other row has a verb.
   * @param {string} missionId
   * @param {string} cellId
   * @param {{ step: string, run: number }} request
   * @param {string} label
   * @param {string} comments
   */
  /**
   * Can this cell be asked anything yet?
   *
   * False for exactly one window: a cell activated since the last tick, whose meta-thread the
   * orchestrator has not created yet. Every surface offering a transition gates on it, so the
   * window shows no controls rather than controls that would fail.
   */
  canIntend(missionId, cellId) {
    return this._threadExists(cellMetaThreadAlias(missionId, cellId));
  }

  /**
   * Ask the cell for something — ONE record, one syntax, for every verb it publishes.
   *
   * `answer`, `resume`, `restart`, `message`: the verb is what varies and the payload is the same
   * shape every time. There were three methods and three shapes here — a gate's `label`, an
   * in-flight prompt's `answers`, a resting cell's text — for one act, and the client had to know
   * which was which. It reads the verbs off `cell.props.request` now and posts whichever was
   * pressed.
   *
   * **A VERB, never a target status.** The orchestrator decides what a verb means for the status the
   * cell is actually in and is the only writer of `props.status`, which is what makes the state
   * machine enforceable rather than aspirational. This client used to write statuses itself, from
   * four places, unguarded — racing the tick's own `if_rev` write and able to leave a cell in a
   * state no edge leads to. `activateCell` is the one that stays a status write, because a draft is
   * invisible to the heartbeat and so has no second writer to race (06 §6.4).
   *
   * **`answers` are the whole of an answer.** One entry per question, in the harness's own shape,
   * which is the only way a prompt of several questions can be answered correctly — mapped from one
   * string instead, a four-question prompt comes back answered the same way four times. A gate's
   * routing label is among them, as the answer to the question the request marked `routes`. The
   * `text` beside them is a summary for a person reading the feed; neither is derivable from the
   * other.
   *
   * Two writes, and both are needed:
   *
   *  - the RECORD is the request, durable and ordered on the feed the outcome will appear in. It is
   *    also what a refresh recovers "pending" from, so nothing about the in-flight state is held in
   *    this tab;
   *  - and the session is asked to WATCH. Somebody who just told a cell to do something is asking
   *    to see it happen, and the orchestrator takes up to a tick to pick the ask up — a board that
   *    does not move in that minute reads as a button that did nothing, which is what it looked
   *    like. The chat widget has done this on send since it was written; this is the same act.
   *
   *  - `intent_pending` is a WAKE-UP BIT. `inactive`, `failed` and `completed` are excluded from the
   *    heartbeat's own cell query, so a record posted on one of those would never be read at all.
   *    Set unconditionally: which statuses the tick's query excludes is exactly the kind of thing
   *    this surface should not know, and the orchestrator clears it in the same write that applies
   *    what it found.
   *
   * **The meta-thread has to exist already**, and this does not create one. The orchestrator owns
   * that object and brings it into existence on a cell's first reconcile, so between activating a
   * cell and the tick that picks it up there is nowhere for an intent to land — and the honest thing
   * for a surface to do in that window is to offer no transition at all (`canIntend`), rather than
   * mint a thread the orchestrator is about to write into. The platform refuses the post outright in
   * any case: `post_session_thread_item` does not auto-create.
   *
   * @param {string} verb  - answer | resume | restart | message | pause
   * @param {string} text  - the person's own words; may be empty
   * @param {{ answers?: Array, step?: string, run?: number }} scope - the answers, and the run this
   *        is about (an answer and a `message` both name one)
   */
  async postIntent(missionId, cellId, verb, text = '', scope = {}) {
    this._assertMutable(verb);
    const sessionId = this.resolveSession(missionId);
    const alias = cellMetaThreadAlias(missionId, cellId);
    const metadata = { type: "intent", source: "external", event: verb };
    if (scope.step !== undefined) metadata.step = scope.step;
    if (scope.run !== undefined) metadata.run = scope.run;
    if (Array.isArray(scope.answers) && scope.answers.length > 0) metadata.answers = scope.answers;
    await this._api.threadItemPost(
      this._revisionId, sessionId, alias, [{ type: "text", text }], undefined, metadata
    );
    // `enableSessionSync`, not a hold: this is a person acting, so it lifts a switch they turned
    // off as well as starting a watch that had wound down. A hold cannot, by design.
    if (this._sessionManager && this._sessionManager.enableSessionSync) {
      this._sessionManager.enableSessionSync(true);
    }
    return this.setCellProps(missionId, cellId, { intent_pending: true });
  }
}
